import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import goalService from '../../services/goalService';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Icon from '../../components/AppIcon';
import GoalCard from './components/GoalCard';
import AddGoalModal from './components/AddGoalModal';
import EditGoalModal from './components/EditGoalModal';

const FinancialGoals = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [goals, setGoals] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [filterType, setFilterType] = useState('all'); // 'all', 'savings_target', 'debt_payoff'

  useEffect(() => {
    if (user?.id) {
      loadData();
    }
  }, [user?.id, filterType]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const statusFilter = filterType === 'all' ? null : null;
      const [goalsData, statsData] = await Promise.all([
        goalService?.getGoals(user?.id, statusFilter),
        goalService?.getGoalStatistics(user?.id)
      ]);
      
      // Apply type filter
      const filteredGoals = filterType === 'all' 
        ? goalsData 
        : goalsData?.filter(g => g?.goalType === filterType);
      
      setGoals(filteredGoals);
      setStatistics(statsData);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddGoal = async (goalData) => {
    try {
      await goalService?.createGoal({
        ...goalData,
        userId: user?.id
      });
      await loadData();
      setShowAddModal(false);
    } catch (err) {
      throw err;
    }
  };

  const handleUpdateGoal = async (goalId, updates) => {
    try {
      await goalService?.updateGoal(goalId, updates);
      await loadData();
      setEditingGoal(null);
    } catch (err) {
      throw err;
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm('Are you sure you want to delete this goal?')) return;
    try {
      await goalService?.deleteGoal(goalId);
      await loadData();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleViewDetails = (goalId) => {
    navigate(`/goal-details-progress?goalId=${goalId}`);
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 100) return 'text-success';
    if (percentage >= 75) return 'text-primary';
    if (percentage >= 50) return 'text-accent';
    return 'text-muted-foreground';
  };

  const getProgressBarColor = (percentage) => {
    if (percentage >= 100) return 'bg-success';
    if (percentage >= 75) return 'bg-primary';
    if (percentage >= 50) return 'bg-accent';
    return 'bg-muted';
  };

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Financial Goals', path: '/financial-goals' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <Icon name="Loader2" size={48} color="var(--color-primary)" className="animate-spin mx-auto mb-16" />
          <p className="text-lg text-muted-foreground">Loading your financial goals...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Financial Goals - Rovyn Budget</title>
        <meta name="description" content="Create and track savings targets and debt payoff goals with progress monitoring and milestone achievements" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          <div className="mx-auto px-16 md:px-24 lg:px-32 py-24 md:py-32 lg:py-40">
            <Breadcrumbs items={breadcrumbItems} />

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-32 gap-16">
              <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground mb-8">
                  Financial Goals
                </h1>
                <p className="text-base md:text-lg text-muted-foreground font-caption">
                  Track your savings targets and debt payoff progress
                </p>
              </div>

              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-8 px-20 py-12 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth font-medium min-h-[44px]"
              >
                <Icon name="Plus" size={20} color="currentColor" />
                Create New Goal
              </button>
            </div>

            {error && (
              <div className="mb-24 p-16 bg-destructive/10 border border-destructive rounded-lg">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {/* Statistics Cards */}
            {statistics && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 md:gap-20 lg:gap-24 mb-32">
                <div className="bg-card rounded-lg p-20 md:p-24 shadow-elevation-2">
                  <div className="flex items-center justify-between mb-12">
                    <Icon name="Target" size={24} color="var(--color-primary)" />
                    <span className="text-2xl font-heading font-semibold text-card-foreground">
                      {statistics?.totalGoals}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">Total Goals</p>
                </div>

                <div className="bg-card rounded-lg p-20 md:p-24 shadow-elevation-2">
                  <div className="flex items-center justify-between mb-12">
                    <Icon name="CheckCircle2" size={24} color="var(--color-success)" />
                    <span className="text-2xl font-heading font-semibold text-card-foreground">
                      {statistics?.completedGoals}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">Completed</p>
                </div>

                <div className="bg-card rounded-lg p-20 md:p-24 shadow-elevation-2">
                  <div className="flex items-center justify-between mb-12">
                    <Icon name="TrendingUp" size={24} color="var(--color-accent)" />
                    <span className="text-2xl font-heading font-semibold text-card-foreground">
                      {statistics?.activeGoals}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">Active Goals</p>
                </div>

                <div className="bg-card rounded-lg p-20 md:p-24 shadow-elevation-2">
                  <div className="flex items-center justify-between mb-12">
                    <Icon name="Percent" size={24} color="var(--color-primary)" />
                    <span className="text-2xl font-heading font-semibold text-card-foreground">
                      {statistics?.overallProgress?.toFixed(1)}%
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">Overall Progress</p>
                </div>
              </div>
            )}

            {/* Filter Tabs */}
            <div className="flex items-center gap-12 mb-24 border-b border-border">
              <button
                onClick={() => setFilterType('all')}
                className={`px-16 py-12 font-caption font-medium transition-smooth min-h-[44px] ${
                  filterType === 'all' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                All Goals
              </button>
              <button
                onClick={() => setFilterType('savings_target')}
                className={`px-16 py-12 font-caption font-medium transition-smooth min-h-[44px] ${
                  filterType === 'savings_target' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                Savings
              </button>
              <button
                onClick={() => setFilterType('debt_payoff')}
                className={`px-16 py-12 font-caption font-medium transition-smooth min-h-[44px] ${
                  filterType === 'debt_payoff' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                Debt Payoff
              </button>
            </div>

            {/* Goals Grid */}
            {goals?.length === 0 ? (
              <div className="text-center py-40">
                <Icon name="Target" size={64} color="var(--color-muted-foreground)" className="mx-auto mb-16" />
                <h3 className="text-xl font-heading font-semibold text-foreground mb-8">
                  No goals yet
                </h3>
                <p className="text-muted-foreground mb-24">
                  Create your first financial goal to start tracking your progress
                </p>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="px-20 py-12 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth font-medium min-h-[44px]"
                >
                  Create Goal
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-20 md:gap-24">
                {goals?.map((goal) => (
                  <GoalCard
                    key={goal?.id}
                    goal={goal}
                    onEdit={() => setEditingGoal(goal)}
                    onDelete={() => handleDeleteGoal(goal?.id)}
                    onViewDetails={() => handleViewDetails(goal?.id)}
                    getProgressColor={getProgressColor}
                    getProgressBarColor={getProgressBarColor}
                  />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      {showAddModal && (
        <AddGoalModal
          userId={user?.id}
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddGoal}
        />
      )}

      {editingGoal && (
        <EditGoalModal
          goal={editingGoal}
          onClose={() => setEditingGoal(null)}
          onUpdate={handleUpdateGoal}
        />
      )}
    </>
  );
};

export default FinancialGoals;